import { AppAudioRecorder } from './appAudioRecorder.ts'
import { AuthStore } from '../../auth/authStore.ts'

interface RecordingItems {
  stream: MediaStream
  recorder: AppAudioRecorder
  streamSource: MediaStreamAudioSourceNode
  audioContext: AudioContext
  analyser: AnalyserNode
}

type AudioSessionType = 'playback' | 'play-and-record'

export class RecordingCancelledError extends Error {
  constructor() {
    super('Recording cancelled')
    this.name = 'AbortError'
  }
}

export class StreamService {
  items?: RecordingItems

  private generation = 0
  private pending?: Promise<AppAudioRecorder>
  private stopping?: Promise<Blob | undefined>

  get isCapturing() {
    return !!(this.items ?? this.pending)
  }

  analyzerData = new Uint8Array()
  constructor(private authStore: AuthStore) {}

  usePlaybackSession() {
    // Global click/touch unlocks must not end a live microphone track.
    if (!this.isCapturing) this.selectAudioSession('playback')
  }

  private selectAudioSession(type: AudioSessionType) {
    const { audioSession } = navigator as Navigator & {
      audioSession?: { type: string }
    }
    if (!audioSession || audioSession.type === type) return

    try {
      audioSession.type = type
    } catch {
      // This optional API must not break recording on unsupported browsers.
    }
  }

  getRecorder(): Promise<AppAudioRecorder> {
    const generation = this.generation
    if (this.stopping) {
      const afterStop = () => {
        this.checkGeneration(generation)
        return this.getRecorder()
      }
      return this.stopping.then(afterStop, afterStop)
    }
    if (this.items) return Promise.resolve(this.items.recorder)
    if (this.pending) return this.pending
    this.selectAudioSession('play-and-record')
    const pending = this.openRecorder(generation)
    this.pending = pending
    const settled = () => {
      if (this.pending !== pending) return
      this.pending = undefined
      this.usePlaybackSession()
    }
    void pending.then(settled, settled)
    return pending
  }

  private checkGeneration(generation: number) {
    if (generation !== this.generation) throw new RecordingCancelledError()
  }

  private async openRecorder(generation: number) {
    const resources: Partial<RecordingItems> = {}

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      })
      resources.stream = stream
      this.checkGeneration(generation)
      const recorder = new AppAudioRecorder(stream)
      const audioContext = new AudioContext()
      resources.audioContext = audioContext
      const analyser = audioContext.createAnalyser()
      resources.analyser = analyser
      analyser.fftSize = 256
      analyser.smoothingTimeConstant = 1
      const streamSource = audioContext.createMediaStreamSource(stream)
      resources.streamSource = streamSource
      streamSource.connect(analyser)
      this.items = { stream, recorder, streamSource, audioContext, analyser }
      this.analyzerData = new Uint8Array(analyser.fftSize)
      this.authStore.trackEvent('microfon_accept')

      return recorder
    } catch (error) {
      if (this.items?.stream === resources.stream) this.items = undefined
      this.releaseResources(resources)

      this.checkGeneration(generation)
      throw Object.assign(
        new Error('Необходимо разрешить доступ к микрофону'),
        { cause: error },
      )
    }
  }

  stopRecording(): Promise<Blob | undefined> {
    this.generation++
    this.pending = undefined
    if (this.stopping) return this.stopping
    const items = this.items
    if (!items) {
      this.usePlaybackSession()
      return Promise.resolve(undefined)
    }
    const stopping = this.finishRecording(items)
    this.stopping = stopping
    const settled = () => {
      if (this.stopping === stopping) this.stopping = undefined
    }
    void stopping.then(settled, settled)
    return stopping
  }

  private async finishRecording(items: RecordingItems) {
    try {
      return await items.recorder.stopRecord()
    } finally {
      // Preserve the final dataavailable chunk before releasing the input.
      if (this.items === items) {
        this.items = undefined
        this.releaseResources(items)
      }
      this.usePlaybackSession()
    }
  }

  private releaseResources(resources: Partial<RecordingItems>) {
    const { stream, audioContext, analyser, streamSource } = resources
    stream?.getTracks().forEach((track) => {
      track.stop()
    })
    analyser?.disconnect()
    streamSource?.disconnect()

    if (!audioContext) return

    // Native close() must not delay releasing the microphone or the next reply.
    void audioContext.close().catch(() => {
      // Tracks and nodes have already been released; do not block the next reply.
    })
  }

  getUpdatedAnalyzerData() {
    if (this.items) {
      this.items.analyser.getByteTimeDomainData(this.analyzerData)
    }
    return this.analyzerData
  }

  getStream() {
    return this.items?.stream
  }

  stopTracks() {
    this.generation++
    this.pending = undefined
    const items = this.items
    this.items = undefined
    this.analyzerData = new Uint8Array()
    if (items) this.releaseResources(items)
    this.usePlaybackSession()
  }
}
